/* ═══════════════════════════════════════════════════════════════
   SYNC — merge3: the three-way Review engine (docs/SYNC_PLAN.md §2.1, D5).

   base   = the version the copy was last in step with
   ours   = the copy as it stands today
   theirs = upstream's new version

   Returns every change upstream made that the copy has not, one item per
   change, for a person to Accept / Reject. Nothing is ever auto-picked.

   Diffing is the libraries' (D5 — hand-built: none):
     · node-diff3 `diffIndices` — word-token hunks base→ours, base→theirs,
       grouped into diff3 regions (overlapping hunks = one region, the
       diff3MergeRegions rule) so each region keeps its offsets in all three.
     · jsdiff `diffArrays` — block-id order, for upstream moves.

   Pure. No React, no I/O, no Yjs.
   ═══════════════════════════════════════════════════════════════ */

import { diffIndices } from 'node-diff3';
import { diffArrays } from 'diff';
import type { ContentBlock, TableData } from '../spec';
import { CONTENT_KEYS, stableStringify } from '../spec-compose';
import type { Change, ChangeKind } from './types';
import { spanText, tokenize } from './tokens';

/* ─── Word regions ─── */

export interface WordRegion {
  kind: Exclude<ChangeKind, 'moved'>;
  /** Word offsets in OURS (end exclusive). */
  start: number;
  end: number;
  before: string;
  after: string;
  /** Set only on a true conflict (both sides changed the span differently). */
  oursText?: string;
}

interface Hunk {
  side: 'a' | 'b';
  oStart: number;
  oLength: number;
  abLength: number;
}

const sameWords = (x: string[], y: string[]): boolean =>
  x.length === y.length && x.every((w, i) => w === y[i]);

/** Three-way word merge of one text. Only regions upstream touched come back. */
export function mergeWords(baseText: string, oursText: string, theirsText: string): WordRegion[] {
  const o = tokenize(baseText);
  const a = tokenize(oursText);
  const b = tokenize(theirsText);
  const hunks: Hunk[] = [];
  for (const h of diffIndices(o.words, a.words)) {
    hunks.push({ side: 'a', oStart: h.buffer1[0], oLength: h.buffer1[1], abLength: h.buffer2[1] });
  }
  for (const h of diffIndices(o.words, b.words)) {
    hunks.push({ side: 'b', oStart: h.buffer1[0], oLength: h.buffer1[1], abLength: h.buffer2[1] });
  }
  hunks.sort((x, y) => x.oStart - y.oStart || (x.side === y.side ? 0 : x.side === 'a' ? -1 : 1));

  const out: WordRegion[] = [];
  let offA = 0;
  let offB = 0;
  let i = 0;
  while (i < hunks.length) {
    const rStart = hunks[i].oStart;
    let rEnd = rStart + hunks[i].oLength;
    const group: Hunk[] = [hunks[i++]];
    while (i < hunks.length && hunks[i].oStart <= rEnd) {
      rEnd = Math.max(rEnd, hunks[i].oStart + hunks[i].oLength);
      group.push(hunks[i++]);
    }
    const aS = rStart + offA;
    const bS = rStart + offB;
    for (const h of group) {
      if (h.side === 'a') offA += h.abLength - h.oLength;
      else offB += h.abLength - h.oLength;
    }
    const aE = rEnd + offA;
    const bE = rEnd + offB;
    const ow = o.words.slice(rStart, rEnd);
    const aw = a.words.slice(aS, aE);
    const bw = b.words.slice(bS, bE);
    if (sameWords(bw, ow)) continue; // only the copy changed it — not ours to offer
    const before = spanText(o, rStart, rEnd);
    const after = spanText(b, bS, bE);
    if (sameWords(aw, bw)) {
      out.push({ kind: 'reconciled', start: aS, end: aE, before, after });
    } else if (sameWords(aw, ow)) {
      const kind = ow.length === 0 ? 'added' : bw.length === 0 ? 'removed' : 'changed';
      out.push({ kind, start: aS, end: aE, before, after });
    } else {
      out.push({ kind: 'changed', start: aS, end: aE, before, after, oursText: spanText(a, aS, aE) });
    }
  }
  return out;
}

/* ─── Block helpers ─── */

const isWholeOnly = (x: ContentBlock | undefined): boolean =>
  x?.blockType === 'image' || x?.blockType === 'smart-token';

export const shapeOf = (td: TableData | undefined): string =>
  td ? `${td.rows}x${td.cols}:${td.cells.map((r) => r.length).join(',')}` : '';

/** Plain text of a block for whole-block items. */
export function textOf(x: ContentBlock | undefined): string {
  if (!x) return '';
  if (x.blockType === 'table' && x.tableData) {
    return x.tableData.cells.map((r) => r.map((c) => c.text).join(' | ')).join('\n');
  }
  return x.text || '';
}

/** Every content key EXCEPT the words: level, emphasis, table structure,
 *  image, smart-token params. Runs count only when `withRuns` (the words are
 *  equal on all three sides, so emphasis is comparable). */
export function attrKey(x: ContentBlock, withRuns: boolean): string {
  const src = x as unknown as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  for (const key of CONTENT_KEYS) {
    if (key === 'text') continue;
    if (key === 'runs' && !withRuns) continue;
    if (key === 'tableData') {
      if (x.tableData) {
        picked.tableData = {
          ...x.tableData,
          cells: x.tableData.cells.map((r) =>
            r.map((c) => {
              const { text: _text, runs, ...rest } = c;
              return withRuns && runs ? { ...rest, runs } : rest;
            })
          ),
        };
      }
      continue;
    }
    if (src[key] !== undefined) picked[key] = src[key];
  }
  picked.smartBlockId = x.smartBlockId;
  picked.smartBlockKind = x.smartBlockKind;
  picked.smartParams = x.smartParams;
  return stableStringify(picked);
}

function wordsEqual(x: string | undefined, y: string | undefined): boolean {
  return sameWords(tokenize(x || '').words, tokenize(y || '').words);
}

function textsEqualEverywhere(b: ContentBlock, o: ContentBlock, t: ContentBlock): boolean {
  if (!wordsEqual(b.text, o.text) || !wordsEqual(b.text, t.text)) return false;
  if (b.tableData || o.tableData || t.tableData) {
    const s = shapeOf(b.tableData);
    if (s !== shapeOf(o.tableData) || s !== shapeOf(t.tableData)) return false;
    const bc = b.tableData?.cells ?? [];
    const oc = o.tableData?.cells ?? [];
    const tc = t.tableData?.cells ?? [];
    for (let r = 0; r < bc.length; r++) {
      for (let c = 0; c < bc[r].length; c++) {
        if (!wordsEqual(bc[r][c].text, oc[r][c].text) || !wordsEqual(bc[r][c].text, tc[r][c].text)) {
          return false;
        }
      }
    }
  }
  return true;
}

/** Whole-block content equality (words compared whitespace-insensitively). */
function sameContent(x: ContentBlock, y: ContentBlock): boolean {
  const words = textsEqualEverywhere(x, x, y);
  return words && attrKey(x, true) === attrKey(y, true);
}

/* ─── merge3 ─── */

interface Pending {
  key: number;
  sub: number;
  seq: number;
  change: Omit<Change, 'id'>;
}

export function merge3(
  base: ContentBlock[],
  ours: ContentBlock[],
  theirs: ContentBlock[]
): Change[] {
  const baseList = base || [];
  const oursList = ours || [];
  const theirsList = theirs || [];
  const B = new Map(baseList.map((x) => [x.id, x]));
  const O = new Map(oursList.map((x) => [x.id, x]));
  const T = new Map(theirsList.map((x) => [x.id, x]));
  const oursIndex = new Map(oursList.map((x, i) => [x.id, i]));
  const theirsIndex = new Map(theirsList.map((x, i) => [x.id, i]));

  const pending: Pending[] = [];
  let seq = 0;
  const push = (key: number, sub: number, change: Omit<Change, 'id'>): void => {
    pending.push({ key, sub, seq: seq++, change });
  };

  /** Nearest preceding block in `list` (before `idx`) that the copy has. */
  const anchorIn = (list: ContentBlock[], idx: number, skip?: string): string | null => {
    for (let k = idx - 1; k >= 0; k--) {
      const id = list[k].id;
      if (id !== skip && O.has(id)) return id;
    }
    return null;
  };
  const posOf = (anchor: string | null): number =>
    anchor === null ? -0.5 : (oursIndex.get(anchor) ?? 0) + 0.5;

  const blockItems = (b: ContentBlock, o: ContentBlock, t: ContentBlock): void => {
    const key = oursIndex.get(o.id) ?? 0;
    const sub = theirsIndex.get(t.id) ?? 0;
    const withRuns = textsEqualEverywhere(b, o, t);
    const kb = attrKey(b, withRuns);
    const ko = attrKey(o, withRuns);
    const kt = attrKey(t, withRuns);

    if (isWholeOnly(b) || isWholeOnly(o) || isWholeOnly(t)) {
      if (kt === kb && wordsEqual(b.text, t.text)) return;
      if (kt === ko && wordsEqual(o.text, t.text)) {
        push(key, sub, { kind: 'reconciled', blockId: o.id, whole: true, before: textOf(b), after: textOf(t) });
        return;
      }
      const conflict = ko !== kb || !wordsEqual(b.text, o.text);
      push(key, sub, {
        kind: 'changed',
        blockId: o.id,
        whole: true,
        before: textOf(b),
        after: textOf(t),
        ...(conflict ? { oursText: textOf(o) } : {}),
        block: t,
      });
      return;
    }

    // Formatting / structure — everything but the words.
    if (kt !== kb) {
      if (kt === ko) {
        push(key, sub, { kind: 'reconciled', blockId: o.id, whole: true, before: '', after: '' });
      } else {
        push(key, sub, {
          kind: 'changed',
          blockId: o.id,
          whole: true,
          before: '',
          after: '',
          ...(ko !== kb ? { oursText: textOf(o) } : {}),
          block: t,
        });
      }
    }

    const emit = (
      regions: WordRegion[],
      runsPresent: boolean,
      cell?: { row: number; col: number }
    ): void => {
      for (const r of regions) {
        push(key, sub, {
          kind: r.kind,
          blockId: o.id,
          whole: false,
          ...(cell ? { cell } : {}),
          range: { start: r.start, end: r.end },
          before: r.before,
          after: r.after,
          ...(r.oursText !== undefined ? { oursText: r.oursText } : {}),
          ...(runsPresent && r.kind !== 'reconciled' ? { block: t } : {}),
        });
      }
    };

    // Words: a paragraph's live in `text`, a table's per cell (principle 9).
    emit(mergeWords(b.text, o.text, t.text), !!t.runs?.length);
    if (b.tableData && o.tableData && t.tableData) {
      const s = shapeOf(b.tableData);
      if (s === shapeOf(o.tableData) && s === shapeOf(t.tableData)) {
        const bc = b.tableData.cells;
        for (let row = 0; row < bc.length; row++) {
          for (let col = 0; col < bc[row].length; col++) {
            const tc = t.tableData.cells[row][col];
            emit(
              mergeWords(bc[row][col].text, o.tableData.cells[row][col].text, tc.text),
              !!tc.runs?.length,
              { row, col }
            );
          }
        }
      }
    }
  };

  // Blocks upstream has.
  theirsList.forEach((t, ti) => {
    const b = B.get(t.id);
    const o = O.get(t.id);
    if (!b && !o) {
      // Principle 2 — upstream added it.
      const after = anchorIn(theirsList, ti);
      push(posOf(after), ti, {
        kind: 'added',
        blockId: t.id,
        whole: true,
        anchor: { after },
        before: '',
        after: textOf(t),
        block: t,
      });
    } else if (!b && o) {
      // Both sides hold a block base never had (e.g. accepted earlier).
      if (sameContent(o, t)) {
        push(oursIndex.get(o.id) ?? 0, ti, {
          kind: 'reconciled',
          blockId: o.id,
          whole: true,
          before: '',
          after: textOf(t),
        });
      } else {
        // No base: OURS stands in for it (its level, formatting, table shape
        // and words), so every upstream difference is offered as a change and
        // nothing the two already share is counted as reconciled.
        blockItems(o, o, t);
      }
    } else if (b && !o) {
      // Principle 5 — the copy deleted it, upstream edited it since.
      if (!sameContent(b, t)) {
        const after = anchorIn(theirsList, ti);
        push(posOf(after), ti, {
          kind: 'added',
          blockId: t.id,
          whole: true,
          anchor: { after },
          before: '',
          after: textOf(t),
          block: t,
        });
      }
    } else if (b && o) {
      blockItems(b, o, t);
    }
  });

  // Blocks upstream removed.
  baseList.forEach((b, bi) => {
    if (T.has(b.id)) return;
    const o = O.get(b.id);
    if (o) {
      // Principle 1.
      push(oursIndex.get(o.id) ?? 0, -1, {
        kind: 'removed',
        blockId: b.id,
        whole: true,
        before: textOf(o),
        after: '',
      });
    } else {
      // Both removed it.
      push(posOf(anchorIn(baseList, bi)), -1, {
        kind: 'reconciled',
        blockId: b.id,
        whole: true,
        before: textOf(b),
        after: '',
      });
    }
  });

  // Principle 6 — moves, compared over the blocks all three sides hold.
  const common = (list: ContentBlock[]): string[] =>
    list.filter((x) => B.has(x.id) && O.has(x.id) && T.has(x.id)).map((x) => x.id);
  const movedBetween = (from: string[], to: string[]): Set<string> => {
    const removed = new Set<string>();
    const added = new Set<string>();
    for (const part of diffArrays(from, to)) {
      if (part.removed) part.value.forEach((id) => removed.add(id));
      else if (part.added) part.value.forEach((id) => added.add(id));
    }
    return new Set([...removed].filter((id) => added.has(id)));
  };
  const Bc = common(baseList);
  const Oc = common(oursList);
  const Tc = common(theirsList);
  const movedUp = movedBetween(Bc, Tc);
  if (movedUp.size) {
    const movedOurs = movedBetween(Bc, Oc);
    const prevIn = (list: string[], id: string): string | null => {
      const i = list.indexOf(id);
      return i > 0 ? list[i - 1] : null;
    };
    theirsList.forEach((t, ti) => {
      if (!movedUp.has(t.id)) return;
      const after = anchorIn(theirsList, ti, t.id);
      if (!movedOurs.has(t.id)) {
        push(posOf(after), ti, {
          kind: 'moved',
          blockId: t.id,
          whole: true,
          anchor: { after },
          before: '',
          after: textOf(t),
        });
      } else if (prevIn(Oc, t.id) === prevIn(Tc, t.id)) {
        push(posOf(after), ti, {
          kind: 'reconciled',
          blockId: t.id,
          whole: true,
          before: '',
          after: textOf(t),
        });
      }
      // else: the copy moved it somewhere of its own — the copy's call.
    });
  }

  pending.sort((x, y) => x.key - y.key || x.sub - y.sub || x.seq - y.seq);
  const counters = new Map<string, number>();
  return pending.map(({ change }) => {
    const k = `${change.blockId}:${change.kind}`;
    const n = counters.get(k) ?? 0;
    counters.set(k, n + 1);
    return { id: `${k}:${n}`, ...change };
  });
}
